import type { ShapeLocalPoint } from './types/shape-local-point'

/**
 * Convert a viewport (client) point to shape-local document pixels, undoing the canvas zoom and the
 * frame's rotation.
 *
 * @param clientX     Pointer X in viewport pixels (`event.clientX`).
 * @param clientY     Pointer Y in viewport pixels (`event.clientY`).
 * @param hostRect    `getBoundingClientRect()` of the editor overlay (the shape's local 0,0..w,h box).
 * @param scale       Canvas zoom (doc px → screen px multiplier). Falls back to 1 when 0/NaN.
 * @param rotationDeg Frame rotation in degrees (the overlay's `rotate(...)`).
 * @param width       Shape width in document pixels.
 * @param height      Shape height in document pixels.
 * @returns Point in shape-local document pixels (origin at the shape's top-left, pre-rotation).
 */
export function clientPointToShapeLocal(
  clientX: number,
  clientY: number,
  hostRect: { left: number; top: number; width: number; height: number },
  scale: number,
  rotationDeg: number,
  width: number,
  height: number
): ShapeLocalPoint {
  // The element center is invariant under rotation, so it is a stable origin even when `hostRect`
  // is the rotated AABB (whose width/height differ from the shape's own).
  const centerX = hostRect.left + hostRect.width / 2
  const centerY = hostRect.top + hostRect.height / 2
  const s = scale || 1
  // Screen delta from center → document delta from center.
  const dx = (clientX - centerX) / s
  const dy = (clientY - centerY) / s
  // Undo the frame rotation (rotate by −θ about the center).
  const rad = (-rotationDeg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const localCenterX = dx * cos - dy * sin
  const localCenterY = dx * sin + dy * cos

  // Center-origin → top-left origin (the space caret geometry uses).
  return {
    x: localCenterX + width / 2,
    y: localCenterY + height / 2,
  }
}
